import { Link } from 'react-router-dom';
import { ProjectContactForm } from './ProjectContactForm';
import { Phone, MessageCircle, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ReportButton } from '@/components/ReportButton';

interface MarketedBy {
  name: string;
  logoUrl?: string | null;
  slug?: string | null;
}

interface ProjectContactSidebarProps {
  projectId: string;
  projectName?: string;
  referenceCode?: string;
  phone?: string;
  whatsapp?: string;
  marketedBy?: MarketedBy | null;
  assignedAgentId?: string | null;
}

export function ProjectContactSidebar({
  projectId,
  projectName,
  referenceCode,
  phone = '+251',
  whatsapp = '251',
  marketedBy,
  assignedAgentId,
}: ProjectContactSidebarProps) {
  const cleanWa = (whatsapp || '').replace(/\D/g, '');
  return (
    <div className="hidden lg:block space-y-4">
      <div className="border rounded-xl overflow-hidden">
        {marketedBy && (
          <Link
            to={`/agencies/${marketedBy.slug || ''}`}
            className="flex items-center gap-3 p-3 border-b bg-muted/40 hover:bg-muted/60 transition-colors"
          >
            <div className="h-10 w-10 rounded-md border bg-card overflow-hidden flex items-center justify-center shrink-0">
              {marketedBy.logoUrl ? (
                <img src={marketedBy.logoUrl} alt={marketedBy.name} className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-semibold text-primary uppercase tracking-wide">Marketed by</div>
              <div className="text-sm font-bold truncate">{marketedBy.name}</div>
            </div>
          </Link>
        )}

        <div className="flex">
          <Button className="flex-1 rounded-none h-12 text-sm font-bold gap-2" asChild>
            <a href={`tel:${phone}`}>
              <Phone className="h-4 w-4" /> Call Now
            </a>
          </Button>
          <Button variant="outline" className="rounded-none h-12 w-14 border-l-0" asChild>
            <a
              href={`https://wa.me/${cleanWa}?text=${encodeURIComponent(`Hi, I'd like to inquire about ${projectName || 'this project'}${referenceCode ? ` (${referenceCode.toUpperCase()})` : ''}. Please share availability, pricing and next steps.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
            >
              <MessageCircle className="h-5 w-5 text-[hsl(142,70%,45%)]" />
            </a>
          </Button>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">or</span>
            <Separator className="flex-1" />
          </div>

          <ProjectContactForm
            projectId={projectId}
            projectName={projectName}
            referenceCode={referenceCode}
            assignedAgentId={assignedAgentId}
          />
        </div>
      </div>

      <div className="border rounded-xl p-3 bg-muted/40">
        <ReportButton
          targetType="project"
          targetId={projectId}
          variant="outline"
          className="w-full border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground font-semibold text-sm"
          label="Report this project"
        />
      </div>
    </div>
  );
}
